<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <ui-chips v-if="shifts.length" v-model="selectedShift" :options="shifts.map((value) => ({ value }))" :label="$t('shifts')">
        <template #option="{ option }">
          <report-shift-tag :shift="Number(option.value)" long></report-shift-tag>
        </template>
      </ui-chips>
      <ui-segment v-model="selectedLayout" tabs :options="layoutTabs" :label="$t('view')" />
    </report-tools>

    <report-state v-if="isRange" icon="mdi-calendar-today" :title="$t('daily_report')"></report-state>
    <report-state v-else-if="dayLoading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-food-off"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="gm-scroll"
      table-class="gm-orderers"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :totals="categoryTotals"
      :total-label="$t('total')"
    >
      <template #cell-category="{ row, column }">
        <span v-for="(meal, mealIndex) in row.cells[column.key]" :key="mealIndex" class="gm-orderers__line">
          <b v-if="selectedLayout !== 1" class="rd-num">{{ meal.quantity }}<template v-if="selectedLayout === 0">×</template></b>
          <span v-if="selectedLayout !== 2">{{ meal.name }}</span>
        </span>
        <span v-if="!row.cells[column.key].length" class="rd-dash" aria-hidden="true">–</span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import CompanyReportTabCustomers from "@/components/company/reports/tabs/CompanyReportTabCustomers.vue";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasCompanyGroupOrdererRows from "../../hasCompanyGroupOrdererRows";

/**
 * Naručioci firme tipa "grupa" na računaru: tabela radnika izabrane smene (oznake smena u alatima) sa obrocima po
 * grupama jela i napomenom; prikaz je količine i nazivi / nazivi / količine. Radi samo za jedan dan. Firme i
 * pretragu čita iz zajedničke trake. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopGroupOrderers",
  extends: CompanyReportTabCustomers,
  mixins: [hasCompanyGroupOrdererRows],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    /** Kolone tabele: ime, kategorije jela i napomena. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "fullname", label: this.$t("name_surname"), name: true, title: (row) => `ID ${row.userId}` },
        ...this.mealCategories.map((category) => ({
          key: category,
          slot: "category",
          label: this.dayCategoryLabels[category],
          cellCls: "gm-orderers__cell",
        })),
        { key: "note", label: this.$t("note"), cellCls: "gm-orderers__note" },
      ];
    },
    /** Zbir po kategoriji (0 kad nema). */
    categoryTotals() {
      const result = {};
      this.mealCategories.forEach((category) => {
        result[category] = this.totals[category] || 0;
      });
      return result;
    },
    layoutTabs() {
      // četvrti izbor starog prikaza ("Rezervne obroke") nikad nije imao prikaz, pa ga ovde nema
      return this.layouts.filter((layout) => layout.value < 3).map(({ value, text }) => ({ value, label: text }));
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_orders_found");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.rows.length, tone: "inverse" },
        { key: "meals", label: this.$t("report_desktop.total_meals"), value: this.mealTotal },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    dayLoading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = ["#", this.$t("name_surname"), ...this.mealCategories.map((category) => this.dayCategoryLabels[category]), this.$t("note")];
      const lines = (meals) =>
        meals
          .map((meal) => (this.selectedLayout === 2 ? meal.quantity : this.selectedLayout === 1 ? meal.name : `${meal.quantity}× ${meal.name}`))
          .join("; ");
      const rows = this.rows.map((row, index) => [
        index + 1,
        row.fullname,
        ...this.mealCategories.map((category) => lines(row.cells[category])),
        row.note,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
